/**
 * The wall's add menu: one list, one instance (`WallView`), for the
 * header's + and a right-click on empty wall space (frame menu audit 2026-10-27): New note · Paste as note
 * · New terminal │ Add files…. A note and a terminal go through the shell's
 * tool handlers (each shown only where the shell has one), Paste as note
 * makes a note of the clipboard's text, and Add files… adds any files from
 * a picker, as a drop on the wall does. The desktop app opens its own
 * picker (all files), which shows above the Workspace overlay.
 */
import { useRef } from "react";
import { useWorkspaceTools } from "@/components/layout/workspace-tools-context";
import { FRAME_TYPE_ICONS, Icons } from "@/components/ui";
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuSeparator,
  type ContextMenuAnchor,
} from "@/components/ui/context-menu";
import { pickNativePaths } from "@/desktop/pick-native-files";
import { useT } from "@/i18n";
import { wallMessages } from "@/i18n/messages/wall";
import { isTauri } from "@/lib/is-tauri";
import { pasteClipboardAsNote } from "@/lib/workspace-notes";
import { WallChromeButton } from "./wall-chrome-button";
import { addFilesToWall, addPathsToWall } from "./wall-file-import";

const NoteIcon = FRAME_TYPE_ICONS.note;
const TerminalIcon = FRAME_TYPE_ICONS.terminal;
const FileIcon = FRAME_TYPE_ICONS.file;

interface WallAddMenuProps {
  projectId: string;
  /** Where it opens (the + button, a right-click point); `null` closes it. */
  anchor: ContextMenuAnchor | null;
  onClose: () => void;
}

export function WallAddMenu({ projectId, anchor, onClose }: WallAddMenuProps) {
  const { onAddNote, onAddTerminal } = useWorkspaceTools();
  const inputRef = useRef<HTMLInputElement>(null);
  const t = useT(wallMessages);
  const pickFiles = () => {
    if (!isTauri()) {
      inputRef.current?.click();
      return;
    }
    void pickNativePaths({ multiple: true }).then((paths) =>
      addPathsToWall(paths, projectId),
    );
  };
  // A note needs the shell's open project, as Paste as note does.
  const notes = onAddNote !== undefined;
  return (
    <>
      <ContextMenu anchor={anchor} onClose={onClose}>
        <ContextMenuContent className="min-w-56">
          {notes ? (
            <ContextMenuItem
              icon={<NoteIcon />}
              onClick={onAddNote}
              data-testid="wall-add-note"
            >
              {t("newNote")}
            </ContextMenuItem>
          ) : null}
          {notes ? (
            <ContextMenuItem
              icon={<Icons.ClipboardPaste />}
              onClick={() => void pasteClipboardAsNote()}
              data-testid="wall-add-paste-note"
            >
              {t("pasteAsNote")}
            </ContextMenuItem>
          ) : null}
          {onAddTerminal ? (
            <ContextMenuItem
              icon={<TerminalIcon />}
              onClick={onAddTerminal}
              data-testid="wall-add-terminal"
            >
              {t("newTerminal")}
            </ContextMenuItem>
          ) : null}
          {notes || onAddTerminal ? <ContextMenuSeparator /> : null}
          <ContextMenuItem
            icon={<FileIcon />}
            trailing={
              <span className="pl-3 text-appkit-caption text-muted">
                {t("orDropOnWall")}
              </span>
            }
            onClick={pickFiles}
            data-testid="wall-add-files"
          >
            {t("addFiles")}
          </ContextMenuItem>
        </ContextMenuContent>
      </ContextMenu>
      <input
        ref={inputRef}
        type="file"
        multiple
        hidden
        onChange={(event) => {
          const input = event.currentTarget;
          void addFilesToWall([...(input.files ?? [])], projectId);
          // The same files picked again still count as a change.
          input.value = "";
        }}
        data-testid="wall-add-files-input"
      />
    </>
  );
}

/** The header's +: opens the wall's add menu under it, right edges lined up. */
export function WallAddButton({
  open,
  onOpen,
}: {
  /** The add menu shows. */
  open: boolean;
  onOpen: (anchor: ContextMenuAnchor) => void;
}) {
  const t = useT(wallMessages);
  return (
    <WallChromeButton
      size="icon"
      className="shrink-0"
      aria-label={t("addToWall")}
      aria-haspopup="menu"
      aria-expanded={open}
      title={t("add")}
      onClick={(event) => {
        const rect = event.currentTarget.getBoundingClientRect();
        onOpen({ rect, align: "end" });
      }}
      data-testid="wall-add-menu"
    >
      <Icons.Plus className="h-4 w-4" aria-hidden="true" />
    </WallChromeButton>
  );
}
